import { t } from '../../../lib/i18n';
import { type AuctionScheduleItem } from '../types';
import { cn, groupAuctionsByTimeframe } from '../utils';
import { TimelineCard } from './timeline-card';

interface Props {
  auctions: AuctionScheduleItem[];
  loading?: boolean;
  className?: string;
}

/**
 * Timeline view — closings bucketed into today / this week / later. Not on the
 * artboard; kept for the operator who reads the week as a sequence, and
 * restyled onto Design D tokens.
 */
export function AuctionScheduleTimelineEditor({ auctions, loading, className }: Props) {
  if (loading) {
    return (
      <section
        className={cn(
          'rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-6 text-center text-[13px] text-cf-ink-subtle lg:px-[18px]',
          className,
        )}
      >
        {t('auctionSchedule.board.loading')}
      </section>
    );
  }

  if (auctions.length === 0) {
    return (
      <section
        className={cn(
          'rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-10 text-center lg:px-[18px]',
          className,
        )}
      >
        <h3 className="text-base font-semibold tracking-[-0.02em]">
          {t('auctionSchedule.timeline.noUpcoming')}
        </h3>
        <p className="mt-1.5 text-[13px] text-cf-ink-subtle">
          {t('auctionSchedule.list.emptyHint')}
        </p>
      </section>
    );
  }

  const timelineGroups = groupAuctionsByTimeframe(auctions, {
    today: t('auctionSchedule.timeline.endingToday'),
    thisWeek: t('auctionSchedule.timeline.endingThisWeek'),
    later: t('auctionSchedule.timeline.endingLater'),
  });

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {timelineGroups.map((group) => (
        <section key={group.timeframe} className="flex flex-col gap-2.5">
          <div className="flex items-baseline gap-2.5">
            <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
              {group.title}
            </h2>
            <span className="font-mono text-[11px] tracking-[0.12em] text-cf-ink-subtle uppercase">
              {group.items.length}
            </span>
          </div>

          <div className="relative flex flex-col gap-2">
            {/* The rail the dots sit on — desktop only; on mobile the cards are
                already a single column and a second vertical line adds nothing. */}
            <span className="absolute inset-y-0 left-[30px] hidden w-px bg-cf-line-soft lg:block" />
            {group.items.map((auction) => (
              <TimelineCard key={auction._id} auction={auction} timeframe={group.timeframe} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
